/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 26/08/2025 - 13:26:08
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 26/08/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
  Button,
  Image,
  Form,
  OverlayTrigger,
  Tooltip,
  Accordion,
} from "react-bootstrap";
import {
  addSlopeArea,
  addTiltedPolygon,
  deletePolygon,
  deletePolygonHeights,
  deleteTiltedAzimuthValue,
  deleteTiltedSwap,
  deleteTiltedchecked,
  removeSlopeArea,
  removeTiltedAverageElevation,
  setTilAzimuthCheck,
  setTilEdgeCheck,
  setTilElevationCheck,
  setTiltedAzimutyhValue,
  setTiltedLowerHeight,
  setTiltedSwap,
  setTiltedUpperHeight,
  settiltedAverageElevation,
} from "../../../../store/reducers/bmapSlice";
import { apis } from "../../../../api/data";
import { batchApis } from "../../../../api/batchApis";
import { toast } from "react-toastify";
import { putData } from '../../../../utils/axios';
import IconWrapper from "../../../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';
import AddTiledPvArea from "../../../../images/Add Tilted PV Area Vector.png";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { IoInformationCircleOutline } from "react-icons/io5";

// Import Firebase
import { doc, getDoc, updateDoc, deleteField, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useParams } from 'react-router-dom';

// Debounce utility to delay Firebase saves until user stops typing
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Type Definitions
interface AzimuthResponse {
  azimuth: number;
}

interface PointData {
  lat: number;
  lon: number;
  elevation: number;
  offset: number;
  tag: string;
}

interface TiltedElevationData {
  isAutoCalculated: boolean;
  manualValue: number | null;
  apiValue: number | null;
}

// Define the consolidated data structure
interface ConsolidatedTiltedPVArea {
  // Polygon coordinates
  polygon?: Array<{lat: number, lng: number}>;
  
  // Azimuth data
  azimuth?: {
    value: number | null;
    isAutoCalculated: boolean;
  };
  
  // Elevation data
  elevation?: {
    value: number | null;
    isAutoCalculated: boolean;
    apiValue: number | null;
    manualValue: number | null;
  };
  
  // Heights
  heights?: {
    upper: number | null;
    lower: number | null;
  };
  
  // Slope angle
  slope?: {
    value: number | null;
    isAutoCalculated: boolean;
  };
  
  // Which edge is being auto-calculated
  edgeChecked?: string | null; // "PVangle", "upperEdge", or "lowerEdge"
  
  // Whether the upper and lower edges are swapped
  swapped?: boolean;
  
  // Individual point elevations
  pointElevations?: number[];
}

// Firebase helper functions for consolidated data structure
// Save a tilted PV area
const saveTiltedPVArea = async (
  projectId: string,
  areaIndex: number,
  areaData: Partial<ConsolidatedTiltedPVArea>
): Promise<void> => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    
    // Create the area ID based on index
    const areaId = `TPV-${areaIndex + 1}`;
    
    // Get existing data to merge with new data
    const projectDoc = await getDoc(projectRef);
    const existingData = projectDoc.exists() ? 
      ((projectDoc.data().tiltedPVAreas || {})[areaId] || {}) : {};
    
    // Special handling for heights to ensure they're saved correctly
    let mergedData: any = {
      ...existingData,
      ...areaData
    };
    
    // If we're updating heights, ensure they're properly structured
    if (areaData.heights) {
      mergedData.heights = {
        ...(existingData.heights || {}),
        ...areaData.heights
      };
      
    }
    
    // Update Firebase with the consolidated data
    await updateDoc(projectRef, {
      [`tiltedPVAreas.${areaId}`]: mergedData
    });
    
    // console.log(`Successfully saved data for ${areaId}`);
  } catch (error) {
    console.error(`Error saving tilted PV area:`, error);
  }
};





// Load all tilted PV areas
const loadTiltedPVAreas = async (projectId: string): Promise<{[areaId: string]: ConsolidatedTiltedPVArea}> => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    
    if (projectDoc.exists()) {
      const data = projectDoc.data();
      return data.tiltedPVAreas || {};
    }
    
    return {};
  } catch (error) {
    console.error("Error loading tilted PV areas:", error);
    return {};
  }
};

// Delete a tilted PV area
const deleteTiltedPVArea = async (
  projectId: string,
  areaIndex: number
): Promise<void> => {
  try {
    // console.log(`Starting deletion of tilted PV area at index ${areaIndex}`);
    const projectRef = doc(db, 'projects', projectId);
    
    // Get current data
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      // console.log("Project document does not exist");
      return;
    }
    
    const data = projectDoc.data();
    // Use a separate variable name to be clear this is the Firebase data
    const allAreas = data.tiltedPVAreas || {};
    
    // console.log("Current areas in Firebase:", Object.keys(allAreas));
    
    // Compute the Firebase ID for the area to delete (convert from 0-based to 1-based)
    const areaIdToDelete = `TPV-${areaIndex + 1}`;
    // console.log(`Area ID to delete: ${areaIdToDelete}`);
    
    // Check if this area exists in Firebase
    if (!allAreas[areaIdToDelete]) {
      // console.log(`Area ${areaIdToDelete} not found in Firebase, cannot delete`);
      return;
    }
    
    // Simple check - if this is the only area, just clear everything
    if (Object.keys(allAreas).length === 1) {
      // console.log("Only one area left, removing entire tiltedPVAreas field");
      await updateDoc(projectRef, {
        tiltedPVAreas: deleteField()
      });
      return;
    }
    
    // IMPORTANT: Two-step deletion process
    // 1. First, create a new object WITHOUT the area to delete
    const remainingAreas: Record<string, any> = {};
    Object.entries(allAreas).forEach(([id, value]) => {
      if (id !== areaIdToDelete) {
        remainingAreas[id] = value;
      }
    });
    
    // console.log(`After removing ${areaIdToDelete}, remaining areas:`, Object.keys(remainingAreas));
    
    // 2. Then, completely clear tiltedPVAreas and set the new value
    // This ensures we don't have any stale data
    await updateDoc(projectRef, {
      tiltedPVAreas: deleteField()
    });
    
    // 3. Now add back all remaining areas
    await updateDoc(projectRef, {
      tiltedPVAreas: remainingAreas
    });
    
    // console.log("Deletion completed successfully");
  } catch (error) {
    console.error(`Error deleting tilted PV area:`, error);
    toast.error("Error deleting tilted PV area");
  }
};

// Migration function to convert old data structure to new format
const migrateToConsolidatedStructure = async (projectId: string): Promise<void> => {
  try {
    // Get the project document
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    
    if (!projectDoc.exists()) {
      // console.log("Project does not exist, nothing to migrate");
      return;
    }
    
    const data = projectDoc.data();
    
    // Check if migration is needed
    if (data.tiltedPVAreas) {
      // console.log("Data already in consolidated format, no migration needed");
      return;
    }
    
    // Get the old data structure elements
    const tiltedPolygons = data.tiltedPolygons || [];
    const tiltedAzimuthValues = data.tiltedAzimuthValue || [];
    const tiltedAzimuthChecked = data.tiltedAzimuthChecked || [];
    const tiltedElevationChecked = data.tiltedElevationChecked || [];
    const tiltedAverageElevation = data.tiltedAverageElevation || [];
    const tiltedLowerHeights = data.tiltedLowerHeights || [];
    const tiltedUpperHeights = data.tiltedUpperHeights || [];
    const tiltedSwap = data.tiltedSwap || [];
    const slopeArea = data.slopeArea || [];
    const tiltedEdgeChecked = data.tiltedEdgeChecked || [];
    const tiltedPolygonAzimuthData = data.tiltedPolygonAzimuthData || [];
    const tiltedPolygonElevation = data.tiltedPolygonElevation || {};
    
    // Create consolidated structure
    const tiltedPVAreas: {[key: string]: ConsolidatedTiltedPVArea} = {};
    
    // Number of areas to process
    const numAreas = Math.max(
      tiltedPolygons.length,
      tiltedAzimuthValues.length,
      tiltedAverageElevation.length,
      tiltedLowerHeights.length,
      tiltedUpperHeights.length
    );
    
    // Process each area
    for (let i = 0; i < numAreas; i++) {
      const areaId = `TPV-${i + 1}`;
      
      const areaData: ConsolidatedTiltedPVArea = {
        polygon: tiltedPolygons[i] || [],
        azimuth: {
          value: tiltedAzimuthValues[i] !== undefined ? parseFloat(tiltedAzimuthValues[i]) : null,
          isAutoCalculated: tiltedAzimuthChecked[i] || false
        },
        elevation: {
          value: tiltedAverageElevation[i] !== undefined ? parseFloat(tiltedAverageElevation[i]) : null,
          isAutoCalculated: tiltedElevationChecked[i] || true,
          apiValue: tiltedPolygonElevation[i]?.apiValue || null,
          manualValue: tiltedPolygonElevation[i]?.manualValue || null
        },
        heights: {
          upper: tiltedUpperHeights[i] !== undefined ? parseFloat(tiltedUpperHeights[i]) : null,
          lower: tiltedLowerHeights[i] !== undefined ? parseFloat(tiltedLowerHeights[i]) : null
        },
        slope: {
          value: slopeArea[i] !== undefined ? parseFloat(slopeArea[i]) : null,
          isAutoCalculated: tiltedEdgeChecked[i] === "PVangle"
        },
        edgeChecked: tiltedEdgeChecked[i] || "PVangle",
        swapped: tiltedSwap[i] || false,
        pointElevations: []
      };
      
      // Add point elevations if available
      if (tiltedPolygonAzimuthData[i] && tiltedPolygonAzimuthData[i].polygonData) {
        const pointData = tiltedPolygonAzimuthData[i].polygonData;
        const pointElevations: number[] = [];
        
        for (let j = 1; j <= 4; j++) {
          const point = pointData[`point${j}`];
          if (point && point.elevation !== undefined) {
            pointElevations.push(point.elevation);
          }
        }
        
        if (pointElevations.length === 4) {
          areaData.pointElevations = pointElevations;
        }
      }
      
      tiltedPVAreas[areaId] = areaData;
    }
    
    // Save the consolidated structure
    await updateDoc(projectRef, { tiltedPVAreas });
    
    // console.log(`Migrated ${numAreas} tilted PV areas to consolidated structure`);
  } catch (error) {
    console.error("Error migrating to consolidated structure:", error);
  }
};

// Component Definition
const TiltedPvAreaPage: React.FC<{ projectName: string }> = ({
  projectName,
}) => {
    const { projectId } = useParams<{ projectId: string | any }>();
  
  const selectedArea = useSelector(
    (state: RootState) => state.bmap.selectedArea
  );
  const polygons = useSelector((state: RootState) => state.bmap.tiltedPolygons);
  const tiltedAzimuth = useSelector(
    (state: RootState) => state.bmap.tiltedAzimuthValue
  );
  const tiltedLowerHeight = useSelector(
    (state: RootState) => state.bmap.tiltedlowerHeights
  );
  const tiltedUpperHeight = useSelector(
    (state: RootState) => state.bmap.tiltedupperHeights
  );
  const swapChecked = useSelector((state: RootState) => state.bmap.tiltedSwap);
  const slopeArea = useSelector((state: RootState) => state.bmap.slopeArea);
  const averageElevation = useSelector(
    (state: RootState) => state.bmap.tiltedAverageElevation
  );
  const checkedElevation = useSelector(
    (state: RootState) => state.bmap.tiltedElevationChecked
  );
  const checkedAzimuth = useSelector(
    (state: RootState) => state.bmap.tiltedAzimuthChecked
  );
  const checkedIndex = useSelector(
    (state: RootState) => state.bmap.tiltedEdgeChecked
  );
  
  const [polygonIndexLength, setPolygonIndexLength] = useState<number>(0);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const dispatch = useDispatch();
  const [length, setLength] = useState<number[]>([]);
  const { t } = useTranslation(['tiltedPvArea', 'common']);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [elevationFetchStatus, setElevationFetchStatus] = useState<{[index: number]: 'pending' | 'fetching' | 'complete' | 'error'}>({});
  const [pointElevations, setPointElevations] = useState<number[][]>(
    Array(polygons.length).fill([])
  );
  const [selectedValueIndex, setSelectedValueIndex] = useState<number>(0);
  const [isDropdownExpanded, setIsDropdownExpanded] = useState<boolean[]>([]);
  const [azimuthDebounceTimers, setAzimuthDebounceTimers] = useState<{[key: number]: NodeJS.Timeout | null}>({});
  const [tiltedElevationData, setTiltedElevationData] = useState<Record<number, TiltedElevationData>>({});
  const [validationErrors, setValidationErrors] = useState<{[index: number]: {upper?: boolean, lower?: boolean}}>({});

  // ✅ Ref to track previous polygon coordinates (avoid JSON.stringify)
  const prevPolygonsRef = useRef<typeof polygons | null>(null);

  // ✅ Ref to store master save timer (so we can cancel it during deletion)
  const masterSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ Ref to track if we're currently deleting (skip master save during deletion)
  const isDeletingRef = useRef<boolean>(false);

  const handleToggle = (index: number) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  const InformationCircleIcon = IoInformationCircleOutline;

  // ❌ REMOVED: Debounced slope save (OPTION A - Phase 2)
  // This inline save is now handled by the master debounced useEffect
  // which saves ALL data after 1 second of inactivity

  // Function to handle checkbox change
  const handleCheckboxChange = (value: string, index: number) => {
    // Calculate the new state
    let newValue: string | null;

    if (checkedIndex[index] === value && checkedIndex[index] !== undefined) {
      // Unchecking - set to null
      newValue = null;
      dispatch(setTilEdgeCheck({ index, check: null }));
    } else {
      // Checking - set to the value
      newValue = value;
      dispatch(setTilEdgeCheck({ index, check: value }));
    }

    // ❌ REMOVED: Inline Firebase saves (OPTION A - Phase 2)
    // Redux state is updated above, Firebase save handled by master useEffect
    // No need for: updateCheckedIndexInFirebase() or saveTiltedPVArea()
  };
  
  // Handle elevation auto-calculate change
  const handleElevationAutoCalculateChange = async (index: number, isChecked: boolean) => {
    if (!projectId) return;
    
    try {
      // Update Redux state
      dispatch(
        setTilElevationCheck({
          index: index,
          check: isChecked,
        })
      );
      
      // Get current elevation data
      const currentData = tiltedElevationData[index] || {
        isAutoCalculated: true,
        manualValue: null,
        apiValue: null
      };
      
      // If turning off auto-calculate, initialize manual value with API value
      const manualValue = !isChecked && currentData.apiValue !== null && currentData.manualValue === null
        ? currentData.apiValue
        : currentData.manualValue;
      
      // Create updated data
      const updatedData: TiltedElevationData = {
        ...currentData,
        isAutoCalculated: isChecked,
        manualValue
      };
      
      // Update local state
      setTiltedElevationData(prev => ({
        ...prev,
        [index]: updatedData
      }));
      
      // Calculate the value to use based on auto/manual setting
      const valueToUse = isChecked && updatedData.apiValue !== null
        ? updatedData.apiValue
        : (!isChecked && manualValue !== null ? manualValue : null);

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect will handle the save after 1 second debounce
      
      // If auto-calculate is now on, update Redux with API value
      if (isChecked && updatedData.apiValue !== null) {
        dispatch(
          settiltedAverageElevation({
            index,
            elevation: updatedData.apiValue.toFixed(1),
          })
        );
      } else if (!isChecked && manualValue !== null) {
        // If auto-calculate is off, update Redux with manual value
        dispatch(
          settiltedAverageElevation({
            index,
            elevation: manualValue.toFixed(1),
          })
        );
      }
    } catch (error) {
      console.error("Error updating elevation auto-calculate:", error);
    }
  };
  
  // Save manual elevation
  const saveManualElevation = async (index: number, value: number | null) => {
    if (!projectId) return;
    
    try {
      // Get current elevation data
      const currentData = tiltedElevationData[index] || {
        isAutoCalculated: false,
        manualValue: null,
        apiValue: null
      };
      
      // Create updated data
      const updatedData: TiltedElevationData = {
        ...currentData,
        isAutoCalculated: false,
        manualValue: value
      };
      
      // Update local state
      setTiltedElevationData(prev => ({
        ...prev,
        [index]: updatedData
      }));

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect will handle the save after 1 second debounce
      
      // Update Redux
      if (value !== null) {
        dispatch(
          settiltedAverageElevation({
            index,
            elevation: value.toFixed(1),
          })
        );
      } else {
        dispatch(
          settiltedAverageElevation({
            index,
            elevation: null,
          })
        );
      }
    } catch (error) {
      console.error("Error saving manual elevation:", error);
    }
  };
  
  // Save manual azimuth
  const saveManualAzimuth = async (index: number, value: number | null) => {
    if (!projectId) return;
    
    try {
      // If value is null or undefined, don't save
      if (value === null || value === undefined) return;
      
      // Update Redux first
      dispatch(
        setTiltedAzimutyhValue({
          index,
          azimuth: value.toString(),
        })
      );

      // ❌ REMOVED: Inline Firebase saves (OPTION A - Phase 2)
      // Master useEffect will handle all saves after 1 second debounce
    } catch (error) {
      console.error(`Error saving manual azimuth for polygon ${index}:`, error);
    }
  };

  // Fetch elevations for polygon points
  const fetchElevations = async (polygonIndex: number) => {
    // Don't process if this polygon is already being fetched or is complete
    if (elevationFetchStatus[polygonIndex] === 'fetching' ||
        elevationFetchStatus[polygonIndex] === 'complete') {
      return;
    }
  
    // Mark as fetching
    setElevationFetchStatus(prev => ({
      ...prev,
      [polygonIndex]: 'fetching'
    }));
  
    // Check if the polygon has 4 points (complete polygon)
    if (polygons[polygonIndex]?.length === 4) {
      try {
        // Prepare all points for batch elevation request
        const points = polygons[polygonIndex].map(point => ({
          lat: point.lat,
          lng: point.lng
        }));

        // Make single batch API call for all 4 points
        const response = await batchApis.getBatchElevation(points);
        const elevations = response.data.elevations;

        // Validate we got all 4 elevations
        if (!elevations || elevations.length !== 4 || elevations.some((e: number | null) => e === null)) {
          console.error(`Failed to fetch all elevations for polygon ${polygonIndex}`, elevations);

          // Mark as error
          setElevationFetchStatus(prev => ({
            ...prev,
            [polygonIndex]: 'error'
          }));

          return; // Exit early if any elevation is null
        }
  
        // Only proceed if we have all 4 elevations
        if (elevations.length === 4) {
          // Update the pointElevations state
          setPointElevations((prevElevations) => {
            const newElevations = [...prevElevations];
            newElevations[polygonIndex] = elevations;
            return newElevations;
          });
  
          // Calculate the average elevation
          const totalElevation = elevations.reduce(
            (acc: number, elevation: number) => acc + elevation,
            0
          );
          const averageElevation = totalElevation / elevations.length;
  
          // Get the current auto-calculate setting
          const isAutoCalculated = checkedElevation[polygonIndex] !== undefined
            ? checkedElevation[polygonIndex]
            : true;
  
          // Get current elevation data
          const currentData = tiltedElevationData[polygonIndex] || {
            isAutoCalculated,
            manualValue: null,
            apiValue: null
          };
  
          // Create updated data
          const updatedData: TiltedElevationData = {
            ...currentData,
            isAutoCalculated,
            apiValue: averageElevation,
            manualValue: (!isAutoCalculated && currentData.manualValue === null)
              ? averageElevation
              : currentData.manualValue
          };
  
          // Update local state
          setTiltedElevationData(prev => ({
            ...prev,
            [polygonIndex]: updatedData
          }));
  
          // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
          // Master useEffect will handle all saves after 1 second debounce
  
          // Update Redux based on auto/manual setting
          if (isAutoCalculated) {
            dispatch(
              settiltedAverageElevation({
                index: polygonIndex,
                elevation: averageElevation.toFixed(1),
              })
            );
          } else if (updatedData.manualValue !== null) {
            dispatch(
              settiltedAverageElevation({
                index: polygonIndex,
                elevation: updatedData.manualValue.toFixed(1),
              })
            );
          }
  
          // Mark as complete
          setElevationFetchStatus(prev => ({
            ...prev,
            [polygonIndex]: 'complete'
          }));
  
          // If we now have all elevation data and azimuth auto-calculate is on,
          // trigger azimuth calculation
          if (checkedAzimuth[polygonIndex]) {
            triggerAzimuthUpdate(polygonIndex);
          }
        }
      } catch (error) {
        console.error(`Error fetching elevations for polygon ${polygonIndex}:`, error);
        // toast.error("Error fetching elevations");
  
        // Mark as error
        setElevationFetchStatus(prev => ({
          ...prev,
          [polygonIndex]: 'error'
        }));
  
        dispatch(
          settiltedAverageElevation({
            index: polygonIndex,
            elevation: null,
          })
        );
      }
    }
  };
  
  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (!projectId) return;
  
      try {
        setIsLoading(true);
        
        // Check if migration is needed
        await migrateToConsolidatedStructure(projectId);
        
        // Load data from the consolidated structure
        const tiltedPVAreas = await loadTiltedPVAreas(projectId);
        
        // Check if we have a mismatch between Redux and Firebase
        // If there are no areas in Firebase but we have polygons in Redux, 
        // we need to clear the Redux state to avoid inconsistency
        if (Object.keys(tiltedPVAreas).length === 0 && polygons.length > 0) {
          // console.log("No areas in Firebase but polygons in Redux, clearing Redux state");
          
          // Clear all polygons from Redux
          for (let i = polygons.length - 1; i >= 0; i--) {
            dispatch(deletePolygon(i));
            dispatch(deletePolygonHeights({ index: i }));
            dispatch(deleteTiltedSwap({ index: i }));
            dispatch(removeTiltedAverageElevation({ index: i }));
            dispatch(deleteTiltedAzimuthValue({ index: i }));
            dispatch(deleteTiltedchecked({ index: i }));
            dispatch(removeSlopeArea(i));
          }
        } else {
          // Process each area and update Redux state
          Object.entries(tiltedPVAreas).forEach(([areaId, data]) => {
            const index = parseInt(areaId.replace('TPV-', '')) - 1;
            
            // Update polygon if available
            if (data.polygon && data.polygon.length > 0) {
              dispatch(
                addTiltedPolygon({
                  index,
                  tiltedPolygon: data.polygon
                })
              );
            }
            
            // Update azimuth
            if (data.azimuth) {
              // Update azimuth value if it exists
              if (data.azimuth.value !== null) {
                dispatch(
                  setTiltedAzimutyhValue({
                    index,
                    azimuth: data.azimuth.value.toString()
                  })
                );
              }
              
              // Update auto-calculate setting
              dispatch(
                setTilAzimuthCheck({
                  index,
                  check: data.azimuth.isAutoCalculated
                })
              );
            }
            
            // Update elevation
            if (data.elevation) {
              // Update auto-calculate setting
              dispatch(
                setTilElevationCheck({
                  index,
                  check: data.elevation.isAutoCalculated
                })
              );
              
              // Set elevation value based on auto/manual setting
              const elevationValue = data.elevation.isAutoCalculated 
                ? data.elevation.apiValue 
                : data.elevation.manualValue;
                
              if (elevationValue !== null) {
                dispatch(
                  settiltedAverageElevation({
                    index,
                    elevation: elevationValue.toFixed(1)
                  })
                );
              }
              
              // Update local state
              setTiltedElevationData(prev => ({
                ...prev,
                [index]: {
                  isAutoCalculated: data.elevation?.isAutoCalculated ?? true,
                  apiValue: data.elevation?.apiValue ?? null,
                  manualValue: data.elevation?.manualValue ?? null
                }
              }));
            }
            
            // Update heights
            if (data.heights) {
              // Update upper height
              if (data.heights.upper !== null) {
                dispatch(
                  setTiltedUpperHeight({
                    index,
                    floatValue: data.heights.upper.toString()
                  })
                );
              }
              
              // Update lower height
              if (data.heights.lower !== null) {
                dispatch(
                  setTiltedLowerHeight({
                    index,
                    lowerFloatValue: data.heights.lower.toString()
                  })
                );
              }
            }
            
            // Update slope
            if (data.slope !== undefined && data.slope !== null) {
              // Handle both old format (just number) and new format (object with isAutoCalculated)
              const slopeValue = typeof data.slope === 'object' && data.slope.value !== undefined
                ? data.slope.value
                : (typeof data.slope === 'number' ? data.slope : null);

              const isAutoCalculated = typeof data.slope === 'object' && data.slope.isAutoCalculated !== undefined
                ? data.slope.isAutoCalculated
                : true; // Default to true for old data

              if (slopeValue !== null && slopeValue !== undefined) {
                dispatch(
                  addSlopeArea({
                    index,
                    value: slopeValue.toString()
                  })
                );
              }

              // Set the edgeChecked based on isAutoCalculated
              if (isAutoCalculated) {
                dispatch(setTilEdgeCheck({ index, check: "PVangle" }));
              } else {
                dispatch(setTilEdgeCheck({ index, check: null }));
              }
            }

            // NOTE: edgeChecked is now handled by slope.isAutoCalculated above (lines 902-907)
            // Removed old edgeChecked loading to prevent overriding slope-based checkbox state

            // Update swapped status
            dispatch(
              setTiltedSwap({
                index,
                checked: data.swapped || false
              })
            );
            
            // Update point elevations if available
            if (data.pointElevations && data.pointElevations.length > 0) {
              setPointElevations(prev => {
                const newElevations = [...prev];
                newElevations[index] = data.pointElevations || [];
                return newElevations;
              });
              
              // Update elevation fetch status
              setElevationFetchStatus(prev => ({
                ...prev,
                [index]: 'complete'
              }));
            }
          });
        }
        
        // After loading, ensure Firebase and Redux are in sync
        await reconcileFirebaseWithReduxState();
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setIsLoading(false);
        toast.error(t('tiltedPvArea:errors.loadingData'));
      }
    };
  
    loadInitialData();
  }, [projectId, dispatch]);


  const handleDonePolygon = async () => {
    // Check if all polygons have their elevations fetched
    const allElevationsFetched = polygons.every((_, index) => 
      elevationFetchStatus[index] === 'complete'
    );
    
    if (!allElevationsFetched) {
      // Attempt to fetch elevations for any missing polygons
      polygons.forEach((_, index) => {
        if (!elevationFetchStatus[index] || elevationFetchStatus[index] === 'pending' || elevationFetchStatus[index] === 'error') {
          fetchElevations(index);
        }
      });
      
      return;
    }
    
    if (!projectId) {
      console.error("Project ID is missing, cannot save data");
      return;
    }
    
    try {
      // Collect indices of polygons that need azimuth calculation
      const azimuthIndices: number[] = [];

      // Process each polygon
      for (let i = 0; i < polygons.length; i++) {
        // Skip this polygon if it doesn't have 4 points of elevation data
        if (!pointElevations[i] || pointElevations[i].length !== 4) {
          continue;
        }

        // Skip if any of the elevations is undefined
        const hasUndefinedElevation = pointElevations[i].some(
          (elevation) => elevation === undefined || elevation === null
        );

        if (hasUndefinedElevation) {
          continue;
        }

        // Skip if there's no height data but auto-azimuth is enabled
        if (
          (tiltedLowerHeight[i] === null || tiltedLowerHeight[i] === undefined) &&
          (tiltedUpperHeight[i] === null || tiltedUpperHeight[i] === undefined) &&
          (checkedAzimuth[i] === true)
        ) {
          dispatch(
            setTiltedAzimutyhValue({
              index: i,
              azimuth: null,
            })
          );
          continue;
        }

        // Collect polygon if it has all required data for azimuth calculation
        if (
          polygons.length >= 1 &&
          pointElevations[i]?.length === 4 &&
          tiltedLowerHeight[i] != null &&
          tiltedUpperHeight[i] != null &&
          checkedAzimuth[i] === true
        ) {
          azimuthIndices.push(i);
        }
      }

      // Calculate azimuth for all polygons in batch
      if (azimuthIndices.length > 0) {
        await calculateBatchAzimuth(azimuthIndices);
      }
    } catch (error) {
      console.error("Error in handleDonePolygon:", error);
      // toast.error("Error saving polygon data");
    }
  };



  // ✅ OPTIMIZED: Trigger elevation fetch when polygons are created or moved (NO JSON.stringify!)
  useEffect(() => {
    // Skip if no polygons or no project ID
    if (polygons.length === 0 || !projectId) return;

    // Skip if still loading initial data from Firebase
    if (isLoading) return;

    // Check if polygons actually changed by comparing with previous value
    const polygonsChanged = !prevPolygonsRef.current ||
      prevPolygonsRef.current.length !== polygons.length ||
      polygons.some((poly, i) => {
        const prevPoly = prevPolygonsRef.current?.[i];
        if (!prevPoly || poly.length !== prevPoly.length) return true;
        return poly.some((point, j) =>
          point.lat !== prevPoly[j]?.lat || point.lng !== prevPoly[j]?.lng
        );
      });

    if (!polygonsChanged) return;

    // Update ref with current polygons
    prevPolygonsRef.current = polygons;

    // Implement a debounce here to avoid too many calculations while dragging
    const debounceTimer = setTimeout(() => {
      // For each polygon, check if it needs elevation data
      polygons.forEach((polygon, index) => {
        // Skip if polygon is incomplete
        if (!polygon || polygon.length !== 4) return;

        // ✅ FIX: Only fetch elevation if we genuinely don't have data yet
        // Don't re-fetch if:
        // 1. Elevation status is already 'complete' AND
        // 2. We have valid point elevations (4 values)
        // This prevents redundant API calls when loading existing data from Firebase
        const hasValidElevationData =
          elevationFetchStatus[index] === 'complete' &&
          pointElevations[index] &&
          pointElevations[index].length === 4 &&
          pointElevations[index].every((elev: number | null) => elev !== null && elev !== undefined);

        // Only fetch if we don't have valid data OR if auto-calculate is enabled and coordinates changed
        if (hasValidElevationData) {
          // We already have valid elevation data from Firebase, skip API call
          return;
        }

        // Reset elevation status to trigger a new fetch
        setElevationFetchStatus(prev => ({
          ...prev,
          [index]: 'pending'
        }));

        // Clear point elevations for this polygon to force refetching
        setPointElevations(prev => {
          const newElevations = [...prev];
          newElevations[index] = [];
          return newElevations;
        });
      });
    }, 1000); // Wait 1 second after polygon movement stops before recalculating

    // Clean up the timer if the effect runs again before the timeout
    return () => clearTimeout(debounceTimer);
  }, [polygons, projectId, isLoading, elevationFetchStatus, pointElevations]);

  // Calculate azimuth for polygon (single)
  const calculateAzimuthForPolygon = async (index: number): Promise<void> => {
    // Skip if auto-calculate is not enabled
    if (!checkedAzimuth[index]) return;

    // Skip if we don't have complete data
    if (
      !polygons[index] ||
      polygons[index].length !== 4 ||
      !pointElevations[index] ||
      pointElevations[index].length !== 4 ||
      tiltedLowerHeight[index] === null ||
      tiltedUpperHeight[index] === null
    ) {
      return;
    }

    try {
      // Prepare polygon data for API call
      const polygon = polygons[index];
      const currentElevations = pointElevations[index];

      const data: {
        area_type: string;
        [key: string]: any;
      } = {
        area_type: "tilted"
      };

      // Prepare the data for each polygon point
      const points = polygon.map((point: { lat: any; lng: any; }, j: number) => {
        const tag = j < 2 ? "upper_edge" : "lower_edge";
        const elevation = currentElevations[j];

        return {
          lat: point.lat,
          lon: point.lng,
          elevation: elevation,
          offset: 0,
          tag,
        };
      });

      // Add point data to the data object
      points.forEach((point, j) => {
        data[`point${j + 1}`] = point;
      });

      // Call the API
      const response = await putData<AzimuthResponse, typeof data>(
        '/azimuth/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Extract the azimuth value
      const azimuthValue = response.azimuth;

      // Update Redux with the azimuth value
      dispatch(
        setTiltedAzimutyhValue({
          index,
          azimuth: Number(azimuthValue).toFixed(1),
        })
      );

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect will handle the save after 1 second debounce
    } catch (error) {
      console.error(`Error calculating azimuth for polygon ${index}:`, error);
      // toast.error("Failed to calculate azimuth");
    }
  };

  // Calculate azimuth for multiple polygons in batch
  const calculateBatchAzimuth = async (indices: number[], forceCalculate: boolean = false): Promise<void> => {
    // Filter to only include polygons with complete data
    // If forceCalculate is true (from toggle), skip the checkedAzimuth check
    const validIndices = indices.filter(index => {
      return (
        (forceCalculate || checkedAzimuth[index]) &&
        polygons[index]?.length === 4 &&
        pointElevations[index]?.length === 4 &&
        tiltedLowerHeight[index] !== null &&
        tiltedUpperHeight[index] !== null
      );
    });

    if (validIndices.length === 0) return;

    try {
      // Prepare batch data
      const pvAreas = validIndices.map(index => {
        const polygon = polygons[index];
        const currentElevations = pointElevations[index];

        const areaData: any = {
          index: index,
          area_type: "tilted"
        };

        // Add 4 points
        polygon.forEach((point: { lat: any; lng: any; }, j: number) => {
          const tag = j < 2 ? "upper_edge" : "lower_edge";
          areaData[`point${j + 1}`] = {
            lat: point.lat,
            lng: point.lng,
            elevation: currentElevations[j],
            offset: 0,
            tag: tag,
          };
        });

        return areaData;
      });

      // Call batch API
      const response = await batchApis.getBatchAzimuth(pvAreas);

      // Process results
      if (response.data?.results) {
        for (const result of response.data.results) {
          if (result.success && result.azimuth !== null) {
            const index = result.index;

            // Update Redux
            dispatch(
              setTiltedAzimutyhValue({
                index,
                azimuth: Number(result.azimuth).toFixed(1),
              })
            );

            // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
            // Master useEffect will handle the save after 1 second debounce
          } else {
            console.error(`Batch azimuth calculation failed for polygon ${result.index}:`, result.error);
          }
        }
      }
    } catch (error) {
      console.error('Error in batch azimuth calculation:', error);
      // Fallback to individual calculations
      for (const index of validIndices) {
        await calculateAzimuthForPolygon(index);
      }
    }
  };
  
  // Handle azimuth auto-calculate change
  const handleAzimuthCheckChange = async (index: number, checked: boolean) => {

    // Update Redux state
    dispatch(
      setTilAzimuthCheck({
        index: index,
        check: checked,
      })
    );

    // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
    // Master useEffect will handle the save after 1 second debounce

    // If turning off auto-calculate, don't do anything else
    if (!checked) {
      return;
    }

    // ✅ FIX ISSUE #2: When turning ON auto-calculate, immediately recalculate
    // to override any manual values using BATCH API
    if (
      polygons[index]?.length === 4 &&
      pointElevations[index]?.length === 4 &&
      tiltedLowerHeight[index] !== null &&
      tiltedUpperHeight[index] !== null
    ) {
      // Use batch API even for single polygon (more efficient)
      // Pass forceCalculate=true to override the checkedAzimuth filter (state might not be updated yet)
      await calculateBatchAzimuth([index], true);
    } else {
      // If we don't have elevation data yet, fetch it first
      if (polygons[index]?.length === 4 && (!pointElevations[index] || pointElevations[index].length !== 4)) {
        await fetchElevations(index);
      }
    }
  };
  
  // Trigger azimuth update with debouncing - USES BATCH API
  const triggerAzimuthUpdate = (index: number): void => {
    // Skip if auto-calculate is not enabled
    if (!checkedAzimuth[index]) return;

    // Clear any existing timer for this index
    const existingTimer = azimuthDebounceTimers[index];
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Create a new timer
    const timer = setTimeout(() => {
      // ✅ Use batch API even for single polygon (more efficient than individual API)
      calculateBatchAzimuth([index]);

      // Clear the timer reference when done
      setAzimuthDebounceTimers(prev => ({
        ...prev,
        [index]: null
      }));
    }, 500); // Debounce delay of 500ms

    // Store the timer reference
    setAzimuthDebounceTimers(prev => ({
      ...prev,
      [index]: timer
    }));
  };
  
  // ✅ FIX: Remove azimuth trigger - heights don't affect direction!
  // Azimuth only depends on polygon coordinates, not height values
  const handleFieldBlur = (index: number): void => {
    // Heights don't affect azimuth, so no calculation needed
    // Azimuth is only recalculated when polygon coordinates change
  };
  
  // Clean up timers when component unmounts
  useEffect(() => {
    return () => {
      // Clear all timers when component unmounts
      Object.values(azimuthDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [azimuthDebounceTimers]);
  
  // Initialize elevation fetching for existing polygons when component mounts
  useEffect(() => {
    // Initialize elevation fetch status for all existing polygons
    if (polygons.length > 0) {
      const initialStatus: {[index: number]: 'pending' | 'fetching' | 'complete' | 'error'} = {};
      
      polygons.forEach((_, index) => {
        // If a polygon already has elevation data, mark it as complete
        if (pointElevations[index]?.length === 4 && averageElevation[index] !== undefined) {
          initialStatus[index] = 'complete';
        } else {
          initialStatus[index] = 'pending';
        }
      });
      
      setElevationFetchStatus(initialStatus);
    }
  }, []);
  
  // Initialize values for new polygons
  useEffect(() => {
    const polygonLength = polygons.length - 1;
    if (polygonLength < 0) return;
    
    dispatch(setTiltedSwap({ index: polygonLength, checked: false }));
    if (checkedElevation[polygonLength] === undefined) {
      dispatch(setTilElevationCheck({ index: polygonLength, check: true }));
    }
    if (checkedAzimuth[polygonLength] === undefined) {
      dispatch(setTilAzimuthCheck({ index: polygonLength, check: true }));
    }
    if (checkedIndex[polygonLength] === undefined) {
      dispatch(setTilEdgeCheck({ index: polygonLength, check: "PVangle" }));
    }
  }, [polygons.length]);
  
  // Delete the polygon and all related data
  const handleDeletePolygon = async (index: number) => {
    try {
      console.log(`🗑️ Deleting tilted polygon at index ${index}`);

      // 1. Remove from Redux
      dispatch(deletePolygon(index));
      dispatch(deletePolygonHeights({ index }));
      dispatch(deleteTiltedSwap({ index }));
      dispatch(removeTiltedAverageElevation({ index }));
      dispatch(deleteTiltedAzimuthValue({ index }));
      dispatch(deleteTiltedchecked({ index }));
      dispatch(removeSlopeArea(index));
      
      // 2. Update local React state by removing the index
      setPointElevations(prev => {
        const newData = [...prev];
        newData.splice(index, 1);
        return newData;
      });
      
      setIsDropdownExpanded(prev => {
        const newData = [...prev];
        newData.splice(index, 1);
        return newData;
      });
      
      // 3. Rebuild indexed objects to shift keys down
      const updatedElevationData: Record<number, TiltedElevationData> = {};
      const updatedFetchStatus: {[index: number]: 'pending' | 'fetching' | 'complete' | 'error'} = {};
      
      Object.entries(tiltedElevationData).forEach(([keyStr, value]) => {
        const key = parseInt(keyStr);
        if (key < index) updatedElevationData[key] = value;
        else if (key > index) updatedElevationData[key - 1] = value;
      });
      
      Object.entries(elevationFetchStatus).forEach(([keyStr, value]) => {
        const key = parseInt(keyStr);
        if (key < index) updatedFetchStatus[key] = value;
        else if (key > index) updatedFetchStatus[key - 1] = value;
      });
      
      setTiltedElevationData(updatedElevationData);
      setElevationFetchStatus(updatedFetchStatus);
      
      setLength(prev => {
        const newLength = [...prev];
        newLength.splice(index, 1);
        return newLength;
      });

      // Note: No manual Firebase updateDoc here! The Redux change will 
      // trigger the master save below, which cleanly overwrites Firebase.

    } catch (error) {
      console.error(`❌ Error deleting tilted polygon:`, error);
      toast.error(t('tiltedPvArea:errors.deletingPolygon'));
    }
  };
  
// This useEffect to handle cleaning up the Firebase data if needed
useEffect(() => {
  // If there are no polygons but we still have tiltedPVAreas in Firebase, clean them up
  const cleanupFirebaseIfNeeded = async () => {
    if (polygons.length === 0 && projectId) {
      try {
        // console.log("No polygons left, ensuring Firebase is clean...");
        const projectRef = doc(db, 'projects', projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (projectDoc.exists()) {
          const data = projectDoc.data();
          if (data.tiltedPVAreas && Object.keys(data.tiltedPVAreas).length > 0) {
            // console.log("Found leftover tiltedPVAreas in Firebase, cleaning up");
            
            // Completely remove the tiltedPVAreas field
            await updateDoc(projectRef, {
              tiltedPVAreas: deleteField()
            });
            
            // console.log("Successfully removed all tiltedPVAreas from Firebase");
          }
        }
      } catch (error) {
        console.error("Error cleaning up Firebase:", error);
      }
    }
  };
  
  cleanupFirebaseIfNeeded();
}, [polygons.length, projectId]);

  // Handle the elevation fetching in a separate useEffect
  useEffect(() => {
    // Check for any polygons needing elevation fetching
    const pendingPolygons = Object.entries(elevationFetchStatus)
      .filter(([_, status]) => status === 'pending')
      .map(([index]) => parseInt(index));

    if (pendingPolygons.length > 0) {
      // Process each pending polygon one at a time
      // fetchElevations() will handle setting status to 'fetching' to prevent duplicates
      pendingPolygons.forEach(index => {
        setTimeout(() => {
          fetchElevations(index);
        }, 300 * index); // Stagger the requests slightly
      });
    }
  }, [elevationFetchStatus]);
  
  // ✅ FIX: Only recalculate azimuth when POLYGON COORDINATES change, not heights!
  // Azimuth (direction) is determined by the polygon's position, not its height
  useEffect(() => {
    // Skip if no polygons
    if (polygons.length === 0) return;

    // Collect all polygon indices that need azimuth update
    const indicesToUpdate: number[] = [];
    polygons.forEach((_, index) => {
      if (checkedAzimuth[index]) {
        indicesToUpdate.push(index);
      }
    });

    if (indicesToUpdate.length === 0) return;

    // Use a debounced trigger to avoid too many API calls
    const timeoutId = setTimeout(() => {
      // Calculate batch azimuth for all polygons at once
      calculateBatchAzimuth(indicesToUpdate);
    }, 500); // Debounce time

    return () => clearTimeout(timeoutId);
  }, [
    // ✅ ONLY depend on polygon coordinates and elevations
    // Heights DON'T affect azimuth/direction!
    JSON.stringify(polygons.map(poly => poly.map(p => [p.lat, p.lng]))),
    JSON.stringify(pointElevations)
  ]);
  
  // ============================================================================
  // ❌ REMOVED: Duplicate handleDonePolygon useEffect (PHASE 1 FIX #3)
  // ============================================================================
  // This was causing DUPLICATE Firebase writes on every state change.
  // The master save useEffect below (line ~2187) handles all Firebase saves
  // with proper 1-second debouncing, eliminating the need for this duplicate.
  //
  // IMPACT: Reduces Firebase writes from ~144 per change to 1 per second.
  // ============================================================================
  
  // Helper functions for input handling
  const handleNumericInput = (value: string): string => {
    // Replace any commas with decimal points
    const normalizedValue = value.replace(/,/g, '.');
    
    // Ensure only one decimal point exists
    let parts = normalizedValue.split('.');
    if (parts.length > 2) {
      // If multiple decimal points, keep only the first one
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    return normalizedValue;
  };
  
  const oneDigitAfterPoint = (enterValue: string): string => {
    // First convert any commas to periods
    let value = handleNumericInput(enterValue);
    
    // Then apply the original decimal place limitation
    if (value.includes(".")) {
      const parts = value.split(".");
      if (parts[1] && parts[1].length > 1) {
        value = `${parts[0]}.${parts[1].slice(0, 1)}`;
        return value;
      }
    }
    return value;
  };
  
  // Update tiltedUpperHeight in Firebase
  const updateTiltedUpperHeightInFirebase = async (index: number, value: string | null) => {
    if (!projectId) return;
  
    try {
      // Convert to number or null
      const upperValue = value !== null ? parseFloat(value) : null;
      const lowerValue = tiltedLowerHeight[index] !== null ? 
        (typeof tiltedLowerHeight[index] === 'string' ? 
          parseFloat(tiltedLowerHeight[index]) : 
          tiltedLowerHeight[index]) : 
        null;
      
      // console.log(`Saving upper height for index ${index}:`, {
      //   upper: upperValue,
      //   lower: lowerValue
      // });

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect will handle the save after 1 second debounce
    } catch (error) {
      console.error(`Error updating tiltedUpperHeight for index ${index}:`, error);
    }
  };
  
  // Update tiltedLowerHeight in Firebase
  const updateTiltedLowerHeightInFirebase = async (index: number, value: string | null) => {
    if (!projectId) return;
  
    try {
      // Convert to number or null
      const upperValue = tiltedUpperHeight[index] !== null ? 
        (typeof tiltedUpperHeight[index] === 'string' ? 
          parseFloat(tiltedUpperHeight[index]) : 
          tiltedUpperHeight[index]) : 
        null;
      const lowerValue = value !== null ? parseFloat(value) : null;
      
      // console.log(`Saving lower height for index ${index}:`, {
      //   upper: upperValue,
      //   lower: lowerValue
      // });

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect will handle the save after 1 second debounce
    } catch (error) {
      console.error(`Error updating tiltedLowerHeight for index ${index}:`, error);
    }
  };
  
  // ❌ REMOVED: updateTiltedSwapInFirebase (OPTION A - Phase 2)
  // ❌ REMOVED: updateCheckedIndexInFirebase (OPTION A - Phase 2)
  // ❌ REMOVED: updateSlopeAreaInFirebase (OPTION A - Phase 2)
  // These functions are no longer needed - Redux state updates trigger
  // the master debounced useEffect which handles all Firebase saves
  
  // Helper function for calculating perpendicular distance
  function computePerpendicularDistance(point: any, linePoint1: any, linePoint2: any) {
    const R = 6371000; // Earth's radius in meters
    const refLat = point.lat * Math.PI / 180;
    
    // Convert lat/lng to local XY coordinates using equirectangular approximation
    const toXY = (pt: any) => ({
      x: R * pt.lng * Math.PI / 180 * Math.cos(refLat),
      y: R * pt.lat * Math.PI / 180,
    });
    
    const A = toXY(linePoint1);
    const B = toXY(linePoint2);
    const C = toXY(point);
    
    // Direction vector from A to B
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    
    // Normal vector to AB (rotated 90° clockwise = (dy, -dx))
    const nx = dy;
    const ny = -dx;
    
    // Vector from A to C
    const px = C.x - A.x;
    const py = C.y - A.y;
    
    // Project AC onto the normal vector (n) → absolute value = distance
    const dot = (px * nx + py * ny);
    const nLength = Math.sqrt(nx * nx + ny * ny);
    const distance = Math.abs(dot) / nLength;
    
    return distance;
  }
  
  // Calculate edge length
  const calculateTheEdgeLength = (
    coord1: { lat: number; lng: number },
    coord2: { lat: number; lng: number }
  ) => {
    if (!google?.maps?.geometry?.spherical) {
      return "0.0";
    }
    
    const length = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(coord1?.lat, coord1?.lng),
      new google.maps.LatLng(coord2?.lat, coord2?.lng)
    );
    return length.toFixed(1);
  };
  
  // Calculate upper/lower edge distance
  useEffect(() => {
    const calculateUpperLowerEdgeDistance = () => {
      const newLength: number[] = [];
  
      polygons.forEach((coordSet: any[], polygonIndex: number) => {
        if (coordSet.length === 4) {
          // Get upper edge points
          const upperEdgePoint1 = coordSet[0];
          const upperEdgePoint2 = coordSet[1];
          
          // Get lower edge points
          const lowerEdgePoint1 = coordSet[2];
          const lowerEdgePoint2 = coordSet[3];
          
          // Calculate midpoints
          const upperEdgeMidpoint = {
            lat: (upperEdgePoint1.lat + upperEdgePoint2.lat) / 2,
            lng: (upperEdgePoint1.lng + upperEdgePoint2.lng) / 2,
          };
          
          const lowerEdgeMidpoint = {
            lat: (lowerEdgePoint1.lat + lowerEdgePoint2.lat) / 2,
            lng: (lowerEdgePoint1.lng + lowerEdgePoint2.lng) / 2,
          };
          
          // Calculate perpendicular distance (average of both directions)
          const distanceLowerToUpper = computePerpendicularDistance(
            lowerEdgeMidpoint,
            upperEdgePoint1,
            upperEdgePoint2
          );
          
          const distanceUpperToLower = computePerpendicularDistance(
            upperEdgeMidpoint,
            lowerEdgePoint1,
            lowerEdgePoint2
          );
          
          // Use the average
          const averageDistance = (distanceLowerToUpper + distanceUpperToLower) / 2;
          
          // Use the new perpendicular distance for the length state
          newLength.push(averageDistance);
        }
      });
  
      setLength(newLength);
    };
  
    calculateUpperLowerEdgeDistance();
  }, [polygons]);
  
  // Calculation functions for slope and edges
  const calculateSlope = (index: number) => {
    if (length[index] && tiltedUpperHeight[index] !== null && tiltedLowerHeight[index] !== null) {
      const heightDifference =
        Number(tiltedUpperHeight[index]) - Number(tiltedLowerHeight[index]);
      const ratio = heightDifference / length[index];
      const angleRad = Math.atan(ratio);
      const angle = Number((angleRad * (180 / Math.PI)).toFixed(1));
      dispatch(addSlopeArea({ index, value: angle }));

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect handles all saves after 1 second debounce

      return angle;
    }
    return 0;
  };
  
  const calculateUpperEdge = (index: number) => {
    if (slopeArea[index] !== undefined && tiltedLowerHeight[index] !== null && length[index] !== undefined) {
      const angleInDegrees = slopeArea[index];
      const angleInRadians = angleInDegrees * (Math.PI / 180);
      const tanValue = Math.tan(angleInRadians) * length[index];
      const upperHeight = Number(tiltedLowerHeight[index]) + Number(tanValue);
      const floatValue = Number(upperHeight.toFixed(1));
      dispatch(setTiltedUpperHeight({ index, floatValue }));

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect handles all saves after 1 second debounce

      return floatValue;
    }
    return 0;
  };
  
  const calculateLowerEdge = (index: number): number => {
    if (slopeArea[index] !== undefined && tiltedUpperHeight[index] !== null && length[index] !== undefined) {
      const angleInDegrees = slopeArea[index];
      const angleInRadians = angleInDegrees * (Math.PI / 180);
      const tanValue = Math.tan(angleInRadians) * length[index];
      const lowerHeight = Number(tiltedUpperHeight[index]) - tanValue;
      const lowerFloatValue = Number(lowerHeight.toFixed(1));
      
      dispatch(setTiltedLowerHeight({
        index,
        lowerFloatValue: lowerFloatValue.toString()
      }));

      // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
      // Master useEffect handles all saves after 1 second debounce

      return lowerFloatValue;
    }
    return 0;
  };
  
  
  // Effect to calculate values based on which edge is being auto-calculated
  useEffect(() => {
    if (checkedIndex[selectedValueIndex] === "PVangle") {
      calculateSlope(selectedValueIndex);
    } else if (checkedIndex[selectedValueIndex] === "lowerEdge") {
      calculateLowerEdge(selectedValueIndex);
    } else if (checkedIndex[selectedValueIndex] === "upperEdge") {
      calculateUpperEdge(selectedValueIndex);
    }
  }, [
    tiltedUpperHeight[selectedValueIndex],
    tiltedLowerHeight[selectedValueIndex],
    slopeArea[selectedValueIndex],
    length[selectedValueIndex],
    checkedIndex[selectedValueIndex],
  ]);
  
  // Toggle dropdown state
  const toggleDropdown = (index: number) => {
    const newDropdownState = [...isDropdownExpanded];
    newDropdownState[index] = !newDropdownState[index];
    setIsDropdownExpanded(newDropdownState);
  };
  
  // Tooltips
  const tooltip = (
    <Tooltip id="tooltip">
      <small>
        {t('tiltedPvArea:tooltips.tiltAngle')}
      </small>
    </Tooltip>
  );
  
  const Azimuthtooltip = (
    <Tooltip id="tooltip">
      <small>
        {t('tiltedPvArea:tooltips.azimuth')}
      </small>
    </Tooltip>
  );
  
  const groundElevationTooltip = (
    <Tooltip id="tooltip">
      <small>
        {t('tiltedPvArea:tooltips.groundElevation')}
      </small>
    </Tooltip>
  );
  
  
  // Set expanded state for newly added polygons
  useEffect(() => {
    if (polygons.length > 0 && polygons.length >= polygonIndexLength) {
      setIsDropdownExpanded((prev) => [
        ...new Array(polygons.length - 1).fill(false),
        true,
      ]);
      setPolygonIndexLength(polygons.length);
    }
  }, [polygons.length]);


// ============================================================================
  // MASTER FIREBASE SAVE - SINGLE SOURCE OF TRUTH
  // ============================================================================
  useEffect(() => {
    if (!projectId || isLoading) return;

    // ✅ DEBOUNCE: Wait 1 second after last change before saving
    const saveTimer = setTimeout(async () => {
      try {
        const projectRef = doc(db, 'projects', projectId);
        
        // If array is empty, wipe the Firebase fields entirely
        if (polygons.length === 0) {
          await updateDoc(projectRef, {
            tiltedPVAreas: deleteField(),
            tiltedPolygons: deleteField(),
            tiltedAzimuthValue: deleteField(),
            tiltedAzimuthChecked: deleteField(),
            tiltedElevationChecked: deleteField(),
            tiltedAverageElevation: deleteField(),
            tiltedLowerHeights: deleteField(),
            tiltedUpperHeights: deleteField(),
            tiltedSwap: deleteField(),
            slopeArea: deleteField(),
            tiltedEdgeChecked: deleteField(),
            tiltedPolygonAzimuthData: deleteField(),
            tiltedPolygonElevation: deleteField()
          });
          return;
        }

        // Construct a fresh, complete object representing current Redux state
        const fullAreasObject: Record<string, any> = {};

        for (let i = 0; i < polygons.length; i++) {
          if (!polygons[i] || polygons[i].length < 4) continue;

          // Always generate sequential IDs based on the CURRENT Redux array
          const areaId = `TPV-${i + 1}`;
          
          fullAreasObject[areaId] = {
            polygon: polygons[i] || [],
            azimuth: {
              value: tiltedAzimuth[i] !== undefined ? tiltedAzimuth[i] : null,
              isAutoCalculated: Boolean(checkedAzimuth[i])
            },
            elevation: {
              value: averageElevation[i] !== undefined ? averageElevation[i] : null,
              isAutoCalculated: Boolean(checkedElevation[i]),
              apiValue: tiltedElevationData[i]?.apiValue ?? null,
              manualValue: tiltedElevationData[i]?.manualValue ?? null
            },
            heights: {
              upper: tiltedUpperHeight[i] !== undefined && tiltedUpperHeight[i] !== null ?
                (typeof tiltedUpperHeight[i] === 'string' ? parseFloat(tiltedUpperHeight[i]) : tiltedUpperHeight[i]) : null,
              lower: tiltedLowerHeight[i] !== undefined && tiltedLowerHeight[i] !== null ?
                (typeof tiltedLowerHeight[i] === 'string' ? parseFloat(tiltedLowerHeight[i]) : tiltedLowerHeight[i]) : null
            },
            slope: {
              value: slopeArea[i] !== undefined && slopeArea[i] !== null ?
                (typeof slopeArea[i] === 'string' ? parseFloat(slopeArea[i]) : slopeArea[i]) : null,
              isAutoCalculated: checkedIndex[i] === "PVangle"
            },
            edgeChecked: checkedIndex[i] || "PVangle",
            swapped: Boolean(swapChecked[i]),
            pointElevations: pointElevations[i] || []
          };
        }

        // Overwrite the field entirely. This guarantees old/zombie keys are wiped.
        if (Object.keys(fullAreasObject).length > 0) {
          await updateDoc(projectRef, {
            tiltedPVAreas: fullAreasObject
          });
        }
      } catch (error) {
        console.error("Error saving all data to Firebase:", error);
      }
    }, 1000); 

    // Cancel the timer if dependencies change before it fires
    return () => clearTimeout(saveTimer);
  }, [
    projectId, isLoading, polygons, tiltedAzimuth, checkedAzimuth, 
    averageElevation, checkedElevation, tiltedElevationData, 
    tiltedUpperHeight, tiltedLowerHeight, slopeArea, checkedIndex, 
    swapChecked, pointElevations
  ]);


  const reconcileFirebaseWithReduxState = async () => {
    if (!projectId) return;
    
    try {
      // console.log("Reconciling Firebase data with Redux state...");
      
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        // console.log("Project document does not exist");
        return;
      }
      
      const data = projectDoc.data();
      const firebaseAreas = data.tiltedPVAreas || {};
      
      // If Redux has no polygons but Firebase has areas, clean Firebase
      if (polygons.length === 0 && Object.keys(firebaseAreas).length > 0) {
        // console.log("Redux has no polygons but Firebase has areas, cleaning Firebase");
        await updateDoc(projectRef, {
          tiltedPVAreas: deleteField()
        });
        return;
      }
      
      // If Redux has polygons but Firebase has none or different count, sync to Firebase
      if (polygons.length > 0) {
        // console.log(`Redux has ${polygons.length} polygons, Firebase has ${Object.keys(firebaseAreas).length} areas`);
        
        // Check if counts don't match
        if (Object.keys(firebaseAreas).length !== polygons.length) {
          // console.log("Count mismatch detected, syncing Firebase with Redux state");
          
          // Create a new areas object
          const updatedAreas: Record<string, any> = {};
          
          // Add each polygon from Redux
          for (let i = 0; i < polygons.length; i++) {
            const areaId = `TPV-${i + 1}`;
            
            // Start with any existing data
            const existingData = firebaseAreas[areaId] || {};
            
            // Create updated area data with current Redux state
            const areaData: Partial<ConsolidatedTiltedPVArea> = {
              ...existingData,
              polygon: polygons[i] || [],
              azimuth: {
                value: tiltedAzimuth[i] !== undefined ? tiltedAzimuth[i] : null,
                isAutoCalculated: Boolean(checkedAzimuth[i])
              },
              elevation: {
                value: averageElevation[i] !== undefined ? averageElevation[i] : null,
                isAutoCalculated: Boolean(checkedElevation[i]),
                apiValue: tiltedElevationData[i]?.apiValue ?? null,
                manualValue: tiltedElevationData[i]?.manualValue ?? null
              },
              heights: {
                upper: tiltedUpperHeight[i] !== undefined && tiltedUpperHeight[i] !== null ? 
                  (typeof tiltedUpperHeight[i] === 'string' ? 
                    parseFloat(tiltedUpperHeight[i]) : 
                    tiltedUpperHeight[i]) : null,
                lower: tiltedLowerHeight[i] !== undefined && tiltedLowerHeight[i] !== null ? 
                  (typeof tiltedLowerHeight[i] === 'string' ? 
                    parseFloat(tiltedLowerHeight[i]) : 
                    tiltedLowerHeight[i]) : null
              },
              slope: {
                value: slopeArea[i] !== undefined && slopeArea[i] !== null ?
                  (typeof slopeArea[i] === 'string' ?
                    parseFloat(slopeArea[i]) :
                    slopeArea[i]) : null,
                isAutoCalculated: checkedIndex[i] === "PVangle"
              },
              edgeChecked: checkedIndex[i] || "PVangle",
              swapped: Boolean(swapChecked[i]),
              pointElevations: pointElevations[i] || []
            };
            
            updatedAreas[areaId] = areaData;
          }
          
          // Two-step update to ensure clean data
          await updateDoc(projectRef, {
            tiltedPVAreas: deleteField()
          });
          
          if (Object.keys(updatedAreas).length > 0) {
            await updateDoc(projectRef, {
              tiltedPVAreas: updatedAreas
            });
          }
          
          // console.log("Successfully reconciled Firebase with Redux state");
        } else {
          // Even if counts match, check each polygon ID to ensure they're sequential
          // This handles cases where middle elements were deleted
          const firebaseIds = Object.keys(firebaseAreas).sort();
          let needsResequencing = false;
          
          for (let i = 0; i < polygons.length; i++) {
            const expectedId = `TPV-${i + 1}`;
            if (i < firebaseIds.length && firebaseIds[i] !== expectedId) {
              needsResequencing = true;
              break;
            }
          }
          
          if (needsResequencing) {
            // console.log("Firebase area IDs need resequencing");
            
            // Create a new areas object with sequential IDs
            const resequencedAreas: Record<string, any> = {};
            
            // Add each polygon from Redux with sequential IDs
            for (let i = 0; i < polygons.length; i++) {
              const areaId = `TPV-${i + 1}`;
              const oldAreaId = firebaseIds[i];
              
              // Use existing data if available, otherwise create new
              const areaData = oldAreaId ? firebaseAreas[oldAreaId] : {
                polygon: polygons[i] || [],
                azimuth: {
                  value: tiltedAzimuth[i] !== undefined ? tiltedAzimuth[i] : null,
                  isAutoCalculated: Boolean(checkedAzimuth[i])
                },
                // Add other properties as needed
              };
              
              resequencedAreas[areaId] = areaData;
            }
            
            // Two-step update to ensure clean data
            await updateDoc(projectRef, {
              tiltedPVAreas: deleteField()
            });
            
            if (Object.keys(resequencedAreas).length > 0) {
              await updateDoc(projectRef, {
                tiltedPVAreas: resequencedAreas
              });
            }
            
            // console.log("Successfully resequenced Firebase area IDs");
          }
        }
      }
    } catch (error) {
      console.error("Error reconciling Firebase with Redux state:", error);
    }
  };

  // ============================================================================
  // MASTER FIREBASE SAVE - SINGLE SOURCE OF TRUTH (PHASE 1 FIX #2)
  // ============================================================================
  // This useEffect handles ALL Firebase saves with proper 1-second debouncing.
  // Previous issue: The cleanup function was running on EVERY dependency change,
  // causing 144+ Firebase writes per polygon change (12 polygons × 12 dependencies).
  //
  // FIXED: Now uses setTimeout with proper debouncing - waits 1 second after the
  // last change before saving, and cancels previous timers.
  //
  // IMPACT: Reduces Firebase writes from ~144 per change to 1 per second maximum.
  // ============================================================================
  useEffect(() => {
    // ✅ CRITICAL: Skip if currently deleting to prevent re-saving deleted data
    if (isDeletingRef.current) {
      console.log("🚫 Master save skipped - deletion in progress");
      return;
    }

    // Skip if not ready
    if (!projectId || isLoading || polygons.length === 0) {
      console.log(`⏭️ Master save skipped - projectId: ${projectId}, isLoading: ${isLoading}, polygons.length: ${polygons.length}`);
      return;
    }

    console.log(`💾 Master save STARTING - polygons.length: ${polygons.length}`);

    // ✅ DEBOUNCE: Wait 1 second after last change before saving
    const saveTimer = setTimeout(async () => {
      // Store timer in ref so handleDeletePolygon can cancel it
      masterSaveTimerRef.current = saveTimer;

      // ✅ CRITICAL: Check deletion flag AGAIN inside the timer callback
      // This prevents saving if a deletion happened while the timer was waiting
      if (isDeletingRef.current) {
        console.log("🚫 Master save timer fired but deletion in progress - ABORTING SAVE");
        return;
      }

      try {
        console.log("💾 Master save timer fired - proceeding with save");

        // First reconcile to ensure consistency
        await reconcileFirebaseWithReduxState();

        // ✅ Check deletion flag AFTER reconcile (in case deletion happened during reconcile)
        if (isDeletingRef.current) {
          console.log("🚫 Deletion detected after reconcile - ABORTING SAVE");
          return;
        }

        // Then save each polygon's data
        for (let i = 0; i < polygons.length; i++) {
          // ✅ Check deletion flag before each save (in case deletion happens mid-save)
          if (isDeletingRef.current) {
            console.log(`🚫 Deletion detected while saving polygon ${i} - ABORTING SAVE`);
            return;
          }

          // Skip incomplete polygons
          if (!polygons[i] || polygons[i].length < 4) continue;

          // Create consolidated data object
          const areaData: Partial<ConsolidatedTiltedPVArea> = {
            polygon: polygons[i] || [],
            azimuth: {
              value: tiltedAzimuth[i] !== undefined ? tiltedAzimuth[i] : null,
              isAutoCalculated: Boolean(checkedAzimuth[i])
            },
            elevation: {
              value: averageElevation[i] !== undefined ? averageElevation[i] : null,
              isAutoCalculated: Boolean(checkedElevation[i]),
              apiValue: tiltedElevationData[i]?.apiValue ?? null,
              manualValue: tiltedElevationData[i]?.manualValue ?? null
            },
            heights: {
              upper: tiltedUpperHeight[i] !== undefined && tiltedUpperHeight[i] !== null ?
                (typeof tiltedUpperHeight[i] === 'string' ?
                  parseFloat(tiltedUpperHeight[i]) :
                  tiltedUpperHeight[i]) : null,
              lower: tiltedLowerHeight[i] !== undefined && tiltedLowerHeight[i] !== null ?
                (typeof tiltedLowerHeight[i] === 'string' ?
                  parseFloat(tiltedLowerHeight[i]) :
                  tiltedLowerHeight[i]) : null
            },
            slope: {
              value: slopeArea[i] !== undefined && slopeArea[i] !== null ?
                (typeof slopeArea[i] === 'string' ?
                  parseFloat(slopeArea[i]) :
                  slopeArea[i]) : null,
              isAutoCalculated: checkedIndex[i] === "PVangle"
            },
            edgeChecked: checkedIndex[i] || "PVangle",
            swapped: Boolean(swapChecked[i]),
            pointElevations: pointElevations[i] || []
          };

          // Single Firebase save per polygon
          await saveTiltedPVArea(projectId, i, areaData);
        }
      } catch (error) {
        console.error("Error saving all data to Firebase:", error);
      }
    }, 1000); // ✅ 1 second debounce - waits for user to stop making changes

    // ✅ Cleanup: Cancel the timer if dependencies change before it fires
    return () => {
      clearTimeout(saveTimer);
      masterSaveTimerRef.current = null; // Clear ref

      // Also clear azimuth timers on cleanup
      Object.values(azimuthDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [
    projectId,
    isLoading,
    polygons,
    tiltedAzimuth,
    checkedAzimuth,
    averageElevation,
    checkedElevation,
    tiltedElevationData,
    tiltedUpperHeight,
    tiltedLowerHeight,
    slopeArea,
    checkedIndex,
    swapChecked,
    pointElevations,
    azimuthDebounceTimers
  ]);

  // ============================================================================
  // BEFOREUNLOAD HANDLER - Save data when browser/tab closes (OPTION A - Phase 2)
  // ============================================================================
  // This ensures that if the user closes the browser or tab before the 1-second
  // debounce completes, their data is still saved to Firebase.
  // ============================================================================
  useEffect(() => {
    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      if (!projectId || polygons.length === 0) return;

      try {
        // Force immediate save on browser close
        await reconcileFirebaseWithReduxState();

        for (let i = 0; i < polygons.length; i++) {
          if (!polygons[i] || polygons[i].length < 4) continue;

          const areaData: Partial<ConsolidatedTiltedPVArea> = {
            polygon: polygons[i] || [],
            azimuth: {
              value: tiltedAzimuth[i] !== undefined ? tiltedAzimuth[i] : null,
              isAutoCalculated: Boolean(checkedAzimuth[i])
            },
            elevation: {
              value: averageElevation[i] !== undefined ? averageElevation[i] : null,
              isAutoCalculated: Boolean(checkedElevation[i]),
              apiValue: tiltedElevationData[i]?.apiValue ?? null,
              manualValue: tiltedElevationData[i]?.manualValue ?? null
            },
            heights: {
              upper: tiltedUpperHeight[i] !== undefined && tiltedUpperHeight[i] !== null ?
                (typeof tiltedUpperHeight[i] === 'string' ?
                  parseFloat(tiltedUpperHeight[i]) :
                  tiltedUpperHeight[i]) : null,
              lower: tiltedLowerHeight[i] !== undefined && tiltedLowerHeight[i] !== null ?
                (typeof tiltedLowerHeight[i] === 'string' ?
                  parseFloat(tiltedLowerHeight[i]) :
                  tiltedLowerHeight[i]) : null
            },
            slope: {
              value: slopeArea[i] !== undefined && slopeArea[i] !== null ?
                (typeof slopeArea[i] === 'string' ?
                  parseFloat(slopeArea[i]) :
                  slopeArea[i]) : null,
              isAutoCalculated: checkedIndex[i] === "PVangle"
            },
            edgeChecked: checkedIndex[i] || "PVangle",
            swapped: Boolean(swapChecked[i]),
            pointElevations: pointElevations[i] || []
          };

          await saveTiltedPVArea(projectId, i, areaData);
        }
      } catch (error) {
        console.error("Error saving data on browser close:", error);
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    projectId,
    polygons,
    tiltedAzimuth,
    checkedAzimuth,
    averageElevation,
    checkedElevation,
    tiltedElevationData,
    tiltedUpperHeight,
    tiltedLowerHeight,
    slopeArea,
    checkedIndex,
    swapChecked,
    pointElevations
  ]);

  return (
    <div>
      {polygons.length < 1 &&
        (selectedArea?.value == "tilted" ||
          selectedArea?.value == "tiltedTab") && (
            <div className="tiltedNothingToShow">
            <div>
              <Image
                src={AddTiledPvArea}
                alt="addTiltedPVarea"
                loading="lazy"
              />
              <strong>{t('tiltedPvArea:emptyState.title')}</strong>
              <small>
                {t('tiltedPvArea:emptyState.description.part1')} <span>{t('tiltedPvArea:emptyState.description.rooftop')}</span> {t('tiltedPvArea:emptyState.description.and')}
                <span>{t('tiltedPvArea:emptyState.description.groundMounted')}</span> {t('tiltedPvArea:emptyState.description.part2')}
              </small>
            </div>
          </div>
        )}
      {polygons.length > 0 &&
        (selectedArea?.value == "tilted" ||
          selectedArea?.value == "tiltedTab") && (
          <div style={{ height: "75vh", overflowY: "auto", width: "100%" }}>
            {polygons.map((polygonData, index) => {
              return (
                <div className="tiltedPVarea mb-2" key={`tiltedPoints${index}`}>
                  <Accordion
                    className="w-100"
                    activeKey={isDropdownExpanded[index] ? `${index}` : null}
                  >
                    <Accordion.Item eventKey={`${index}`}>
                      <Accordion.Header onClick={() => toggleDropdown(index)}>
                      <p className="projectName my-auto">
    {" "}
    {`${t('tiltedPvArea:card.prefix')} - ${index + 1}`} &nbsp; |
    <IconWrapper Icon={MdDeleteOutline} size={17}
      color="red"
      className="mb-1"
      onClick={(e) => {
        e.stopPropagation(); // Prevent accordion from toggling
        handleDeletePolygon(index);
      }}/>
  </p>
  <p className={`collapseBtn my-auto`}>
    {isDropdownExpanded[index] ? (
      <span>
        {t('tiltedPvArea:card.collapse')} <IconWrapper Icon={RxCaretUp} size={20}/>
      </span>
    ) : (
      <span>
        {t('tiltedPvArea:card.expand')} <IconWrapper Icon={RxCaretDown} size={20}/>
      </span>
    )}
  </p>
                      </Accordion.Header>
                      <Accordion.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center">
                        <p className="m-0">
                          {t('tiltedPvArea:sections.areaDimensions')}
                        </p>
                        </div>

                        <div className="d-flex justify-content-between mt-1">
                          <div className="coolinput">
                          <label htmlFor="input" className="text">
                            {t('tiltedPvArea:fields.upperEdgeLength')}
                          </label>
                            <input
                              onChange={() => {}}
                              readOnly
                              type="text"
                              name=" input"
                              className="input disabledInput"
                              value={calculateTheEdgeLength(
                                polygonData[0],
                                polygonData[1]
                              )}
                            />
                          </div>

                          <div className="coolinput">
                          <label htmlFor="input" className="text">
                            {t('tiltedPvArea:fields.lowerEdgeLength')}
                          </label>
                            <input
                              onChange={() => {}}
                              readOnly
                              type="text"
                              name=" input"
                              className="input disabledInput"
                              value={calculateTheEdgeLength(
                                polygonData[2],
                                polygonData[3]
                              )}
                            />
                          </div>
                        </div>
                        <div className="coolinput mt-3">
                        <label
                          htmlFor="input"
                          className="text"
                          style={{ fontSize: "12px", marginBottom: "7px" }}
                        >
                          {t('tiltedPvArea:fields.distanceBetweenEdges')}
                        </label>
                          <input
                            readOnly
                            type="text"
                            name="input"
                            className="input disabledInput"
                            value={`${length[index]?.toFixed(2) || "0.00"}`}
                          />
                        </div>
                        <p className="m-0 mt-4">
                          {t('tiltedPvArea:sections.altitudeParameters')}
                        </p>
                        <div className="d-flex justify-content-between mt-1">
                          <div className="coolinput">
                          <label
                            style={{ color: "#109F0B" }}
                            htmlFor="input"
                            className="text"
                          >
                            {t('tiltedPvArea:fields.upperEdgeHeight')}
                          </label>
                          <input
                            type="text"
                            name="input"
                            disabled={checkedIndex[index] === "upperEdge" ? true : false}
                            className={`${checkedIndex[index] === "upperEdge" ? "disabledInput " : ""}input${validationErrors[index]?.upper ? " error-input" : ""}`}
                            onFocus={() => setSelectedValueIndex(index)}
                            onChange={(e) => {
                              const updatedValue = oneDigitAfterPoint(e.target.value);
                              const hasError = Number(updatedValue) < Number(tiltedLowerHeight[index]);
                              
                              // Update validation state
                              setValidationErrors(prev => ({
                                ...prev,
                                [index]: {
                                  ...prev[index],
                                  upper: hasError
                                }
                              }));
                              dispatch(
                                setTiltedUpperHeight({
                                  index,
                                  floatValue: updatedValue === "" ? null : updatedValue,
                                })
                              );

                              // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
                              // Master useEffect handles all saves after 1 second debounce
                            }}
                            onBlur={() => handleFieldBlur(index)}
                            value={tiltedUpperHeight[index] === null ? "" : tiltedUpperHeight[index]}
                          />
                          {validationErrors[index]?.upper && (
                            <div className="validation-error">
                                <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                              <span>Upper edge height must be greater than lower edge height.</span>
                            </div>
                          )}
                            <div className="d-flex align-items-center">
                              <Form.Check
                                id={`upperEdge-${index}`}
                                onClick={() => {
                                  handleCheckboxChange("upperEdge", index);
                                }}
                                checked={
                                  checkedIndex[index] === "upperEdge"
                                    ? true
                                    : false
                                }
                                className="m-0 p-0"
                                style={{ outline: "none", boxShadow: "none" }}
                                aria-label="option 1"
                              />
                              <label
                                htmlFor={`upperEdge-${index}`}
                                className="ms-2"
                                style={{ fontSize: "12px", color: "#858585 " }}
                              >
                                {t('tiltedPvArea:fields.autoCalculate')}
                              </label>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between">
                            <div className="coolinput">
                            <label
                              style={{ color: "#EA0303" }}
                              htmlFor="input"
                              className="text"
                            >
                              {t('tiltedPvArea:fields.lowerEdgeHeight')}
                            </label>
                            <input
                              type="text"
                              name="input"
                              disabled={checkedIndex[index] === "lowerEdge" ? true : false}
                              onFocus={() => setSelectedValueIndex(index)}
                              className={`${checkedIndex[index] === "lowerEdge" ? "disabledInput " : ""}input${validationErrors[index]?.lower ? " error-input" : ""}`}
                              onChange={(e) => {
                                const value = oneDigitAfterPoint(e.target.value);
                                const hasError = Number(tiltedUpperHeight[index]) < Number(value);
                                
                                // Update validation state
                                setValidationErrors(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    lower: hasError
                                  }
                                }));
                                dispatch(
                                  setTiltedLowerHeight({
                                    index,
                                    lowerFloatValue: value === "" ? null : value,
                                  })
                                );
                                // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
                                // Master useEffect handles all saves after 1 second debounce
                              }}
                              onBlur={() => handleFieldBlur(index)}
                              value={tiltedLowerHeight[index] === null ? "" : tiltedLowerHeight[index]}
                            />
                            {validationErrors[index]?.lower && (
                              <div className="validation-error">
                                 <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                                <span>Upper edge height must be greater than lower edge height.</span>
                              </div>
                            )}
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  id={`lowerEdge-${index}`}
                                  onChange={() => {
                                    handleCheckboxChange("lowerEdge", index);
                                  }}
                                  checked={
                                    checkedIndex[index] === "lowerEdge"
                                      ? true
                                      : false
                                  }
                                  className="shadow-none no-outline outline-0"
                                  style={{
                                    margin: "0px",
                                    lineHeight: "0px",
                                    padding: "0px",
                                  }}
                                  aria-label="option 1"
                                />
                                <label
                                  htmlFor={`lowerEdge-${index}`}
                                  className="ms-2"
                                  style={{
                                    fontSize: "12px",
                                    color: "#858585 ",
                                  }}
                                >
                                  {t('tiltedPvArea:fields.autoCalculate')}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="coolinput">
                            <label
                              style={{ color: "#858585" }}
                              htmlFor="input"
                              className="text"
                            >
                              {t('tiltedPvArea:fields.groundElevation')}
                            </label>
                            <input
                              type="text"
                              name="input"
                              step="0.1"
                              className={checkedElevation[index] ? "disabledInput input" : "input"}
                              disabled={checkedElevation[index] ? true : false}
                              onChange={(e) => {
                                const value = oneDigitAfterPoint(e.target.value);
                                dispatch(
                                  settiltedAverageElevation({
                                    index,
                                    elevation: value === "" ? null : value,
                                  })
                                );
                              }}
                              onBlur={(e) => {
                                if (!checkedElevation[index]) {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  if (value === "") {
                                    saveManualElevation(index, null);
                                  } else {
                                    saveManualElevation(index, parseFloat(value));
                                  }
                                }
                              }}
                              value={averageElevation[index] === null ? "" : averageElevation[index]}
                            />
                          </div>

                          <OverlayTrigger
                            placement="top"
                            overlay={groundElevationTooltip}
                          >
                            <Button variant="none" className="mt-3 p-0">
                              <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                            </Button>
                          </OverlayTrigger>
                        </div>
                        <div className="d-flex align-items-center ">
                          <Form.Check
                            id={`elevation-check-${index}`}
                            checked={checkedElevation[index] || false}
                            onChange={(e) => {
                              // Use the new handler for elevation auto-calculate
                              handleElevationAutoCalculateChange(index, e.target.checked);
                            }}
                          />
                          <label
                            htmlFor={`elevation-check-${index}`}
                            className="ms-2"
                            style={{ fontSize: "12px", color: "#858585 " }}
                          >
                            {t('tiltedPvArea:fields.autoCalculate')}
                          </label>
                        </div>
                        <div className="default mt-4">
                          <div className="d-flex justify-content-between align-items-center px-2 pt-2">
                          <p className="m-0 defaultText">
                            {swapChecked[index] ? t('tiltedPvArea:fields.swapped') : t('tiltedPvArea:fields.default')}
                          </p>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={swapChecked[index] || false}
                                onChange={(e) => {
                                  const upperEdge1 = polygonData[0];
                                  const upperEdge2 = polygonData[1];
                                  const lowerEdge1 = polygonData[2];
                                  const lowerEdge2 = polygonData[3];
                                  const newPolygonData = [
                                    { ...lowerEdge1 },
                                    { ...lowerEdge2 },
                                    { ...upperEdge1 },
                                    { ...upperEdge2 },
                                  ];

                                  dispatch(
                                    addTiltedPolygon({
                                      index,
                                      tiltedPolygon: newPolygonData,
                                    })
                                  );
                                  dispatch(
                                    setTiltedSwap({
                                      index,
                                      checked: e.target.checked,
                                    })
                                  );
                                  // ❌ REMOVED: Inline Firebase save (OPTION A - Phase 2)
                                  // Master useEffect handles all saves after 1 second debounce
                                }}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                          <strong className="floatingLable">
                            {t('tiltedPvArea:fields.swapEdges')}
                          </strong>
                        </div>

                        <p className="m-0 mt-4">
                          {t('tiltedPvArea:sections.angleParameters')} &nbsp;
                        </p>
                        <div>
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="coolinput">
                              <label htmlFor="input" className="text">
                                {t('tiltedPvArea:fields.tiltAngle')}
                              </label>
                              <input
                                onChange={(e) => {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  dispatch(
                                    addSlopeArea({
                                      index,
                                      value: value === "" ? null : value,
                                    })
                                  );
                                  // ❌ REMOVED: Debounced Firebase save (OPTION A - Phase 2)
                                  // Master useEffect handles all saves after 1 second debounce
                                }}
                                type="text"
                                name="input"
                                onFocus={() => setSelectedValueIndex(index)}
                                disabled={checkedIndex[index] === "PVangle" ? true : false}
                                className={checkedIndex[index] === "PVangle" ? "disabledInput input" : "input"}
                                value={slopeArea[index] === null ? "" : slopeArea[index]}
                              />
                              </div>
                              <OverlayTrigger placement="top" overlay={tooltip}>
                                <Button variant="none" className="mt-3 p-0">
                                  <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                                </Button>
                              </OverlayTrigger>
                            </div>
                            <div className="d-flex align-items-center">
                              <Form.Check
                                id={`PVangle-${index}`}
                                checked={
                                  checkedIndex[index] === "PVangle"
                                    ? true
                                    : false
                                }
                                onChange={() => {
                                  handleCheckboxChange("PVangle", index);
                                }}
                              />
                              <label
                                htmlFor={`PVangle-${index}`}
                                className="ms-2"
                                style={{ fontSize: "12px", color: "#858585 " }}
                              >
                                {t('tiltedPvArea:fields.autoCalculate')}
                              </label>
                            </div>
                          </div>
                          <div>
                          <div className="d-flex align-items-center gap-2">
                            <div className="coolinput">
                              <label htmlFor="input" className="text">
                                {t('tiltedPvArea:fields.azimuth')}
                              </label>
                              <input
                                onChange={(e) => {
                                  const value = oneDigitAfterPoint(e.target.value);
                                  dispatch(
                                    setTiltedAzimutyhValue({
                                      index,
                                      azimuth: value === "" ? null : value,
                                    })
                                  );
                                }}
                                type="text"
                                name="input"
                                disabled={checkedAzimuth[index]}
                                className={checkedAzimuth[index] ? "disabledInput input" : "input"}
                                placeholder={checkedAzimuth[index] === true ? "Provide height data first" : ``}
                                value={tiltedAzimuth[index] != null ? tiltedAzimuth[index] : " "}
                                onBlur={(e) => {
                                  if (!checkedAzimuth[index] && e.target.value.trim() !== "" && e.target.value !== " ") {
                                    const value = parseFloat(oneDigitAfterPoint(e.target.value));
                                    if (!isNaN(value)) {
                                      saveManualAzimuth(index, value);
                                    }
                                  }
                                }}
                              />
                            </div>
                            <OverlayTrigger
                              placement="top"
                              overlay={Azimuthtooltip}
                            >
                              <Button variant="none" className="mt-3 p-0">
                                <IconWrapper Icon={IoInformationCircleOutline} size={20} color="#0984E3"/>
                              </Button>
                            </OverlayTrigger>
                          </div>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              id={`azimuth-check-${index}`}
                              checked={checkedAzimuth[index] || false}
                              onChange={(e) => {
                                handleAzimuthCheckChange(index, e.target.checked);
                              }}
                            />
                            <label
                              htmlFor={`azimuth-check-${index}`}
                              className="ms-2"
                              style={{ fontSize: "12px", color: "#858585 " }}
                            >
                              {t('tiltedPvArea:fields.autoCalculate')}
                            </label>
                          </div>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default TiltedPvAreaPage;